import React from 'react';
import { useTranslation } from 'react-i18next';
import { LightningIcons, MagicIcons } from '../../../../../../../icons/icons';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Button, Drawer } from '../../../../../../../ui';
import { FloatingLabelSelect } from '../../../../../../../ui/components/Common/TextFields/OutlinedSelect';
import {
  AddIcon,
  BriefCaseIcon,
  TrashIcon,
} from '../../../../../../../ui/icons/icons';
import {
  AutoAssessment,
  AutoBuildCompleteStatus,
} from '../../../../../../../utils/types/JobOpenings';
import { useQuickBuild } from './Hooks';

interface IQuickBuild {
  isOpen: boolean;
  onCloseDrawer: () => void;
  assessmentId: string;
  autoAssessment: AutoAssessment;
  setAutoAssessment: React.Dispatch<React.SetStateAction<AutoAssessment>>;
  skills: string[];
  jobTitle: string;
  completedElement?: () => void;
  onGenerationComplete?: () => void;
}

// Styled Input Component Helper - Ultra Modern "Ghost" Style
const StyledInput = ({
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
      {...props}
      className={`w-full p-2 border rounded-md outline-none transition-all duration-200 ${
        props.className || ''
      }`}
  />
);

export const QuickBuildDrawer = (props: IQuickBuild) => {
  const {
    isOpen,
    onCloseDrawer,
    autoAssessment,
    setAutoAssessment,
    skills,
    jobTitle,
    completedElement,
    onGenerationComplete,
  } = props;
  const {
    records,
    isSaving,
    generatingQuestions,
    hasUnsavedChanges,
    skillsList,
    questionTypesList,
    levels,
    editedJobTitle,
    handleRemoveSelected,
    handleAddAllSuggestions,
    handleToggleSelectAll,
    handleAddSkill,
    generateQuestions,
    saveSkills,
    handleRemoveSkill,
    handleSelectChange,
    handleInputChange,
    addNewSkillRow,
    setRecords,
    setEditedJobTitle,
  } = useQuickBuild({
    autoAssessment,
    setAutoAssessment,
    skills,
    jobTitle,
    completedElement,
    onGenerationComplete,
  });
  const { t } = useTranslation(NS.adminPages);
  const QB = ADMIN_PAGES_KEYS.jobOpenings.quickBuild;

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onCloseDrawer}
        width='w-full lg:max-w-[94vw]'
        title={t(QB.drawerTitle)}
        footerContent={
          <div className='flex w-full flex-col gap-3 bg-gray-50 sm:flex-row sm:items-center sm:justify-between'>
            <div className='text-sm text-gray-500 font-medium'>
              <span className='text-gray-900'>{ records.length }</span>{ ' ' }
              { t(QB.skillsConfigured) } • { t(QB.totalPoints) }{ ' ' }
              <span className='text-gray-900'>
                { records.reduce((acc, r) => acc + Number(r.points), 0) }
              </span>
            </div>
            <div className='flex gap-3 sm:justify-end'>
              <Button
                  disabled={isSaving || generatingQuestions}
                  label={t(QB.close)}
                  onClick={onCloseDrawer}
                  variant='secondary'
                  className='w-full px-5 border border-gray-300 text-gray-600 hover:text-gray-800 hover:bg-gray-50 bg-white sm:w-auto'
              />
            </div>
          </div>
        }
    >
      <div className='flex flex-col h-full bg-[#f9fafb] text-sm font-sans'>
        <div className='w-full flex flex-col lg:flex-row gap-4 sm:gap-6 h-full'>
          { /* LEFT PANEL: Table */ }
          <div className='w-full lg:w-9/12 flex flex-col gap-6'>
            <div className='bg-white rounded-sm shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] border border-gray-200 overflow-hidden flex flex-col flex-grow'>
              { /* Table Header / Toolbar */ }
              <div className='px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 flex flex-col gap-3 justify-between bg-white sticky top-0 z-10 sm:flex-row sm:items-center'>
                <div className='flex flex-wrap items-center gap-3 sm:gap-4'>
                  <h3 className='font-bold text-gray-800 text-base flex items-center gap-2.5'>
                    <div className='w-1.5 h-6 bg-blue-600 rounded-full'></div>
                    { t(QB.skillConfiguration) }
                  </h3>
                  { records.some((r) => r.checked) && (
                    <button
                        onClick={handleRemoveSelected}
                        className='text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-full transition-colors flex items-center gap-1.5'
                    >
                      <TrashIcon className='w-3.5 h-3.5' />
                      { t(QB.removeSelected) } ({ records.filter((r) => r.checked).length }
                      )
                    </button>
                  ) }
                </div>
                { hasUnsavedChanges && (
                  <div className='flex items-center text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-200'>
                    <span className='w-2 h-2 bg-amber-500 rounded-full mr-2'></span>
                    { t(QB.unsavedChanges) }
                  </div>
                ) }
              </div>

              { /* Scrollable Table Area */ }
              <div className='overflow-auto flex-grow px-3 sm:px-6'>
                { records.length > 0 ? (
                  <table className='w-full text-left text-sm border-separate border-spacing-y-2 mt-2'>
                    <thead>
                      <tr className='text-gray-400 uppercase text-xs tracking-wider font-semibold'>
                        <th className='p-2 w-10 text-center'>
                          <input
                              type='checkbox'
                              checked={
                                records.length > 0 &&
                              records.every((r) => r.checked)
                              }
                              onChange={handleToggleSelectAll}
                              className='rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer'
                          />
                        </th>
                        <th className='p-2 w-1/4'>{ t(QB.colSkill) }</th>
                        <th className='p-2 w-1/6'>{ t(QB.colLevel) }</th>
                        <th className='p-2 w-28 text-center'>{ t(QB.colQs) }</th>
                        <th className='p-2 w-28 text-center'>{ t(QB.colPoints) }</th>
                        <th className='p-2 w-28 text-center'>{ t(QB.colTime) }</th>
                        <th className='p-2 w-1/5'>{ t(QB.colType) }</th>
                        <th className='p-2 w-10'></th>
                      </tr>
                    </thead>
                    <tbody className='text-gray-700'>
                      { records.map((record, index) => (
                        <tr
                            key={index}
                            className='group hover:bg-gray-50/80 transition-all duration-200'
                        >
                          <td className='p-2 rounded-l-lg bg-white border-y border-l border-gray-100 group-hover:border-gray-200 text-center align-middle'>
                            <input
                                type='checkbox'
                                checked={record.checked || false}
                                onChange={() => {
                                  const n = [...records];
                                  n[index].checked = !n[index].checked;
                                  setRecords(n);
                                }}
                                className='rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer'
                            />
                          </td>
                          <td className='p-2 bg-white border-y border-gray-100 group-hover:border-gray-200 align-middle'>
                            <StyledInput
                                value={record.skill}
                                onChange={handleInputChange(index, 'skill')}
                                placeholder='e.g. React'
                                className='font-medium text-gray-900 border-transparent bg-transparent hover:bg-white focus:bg-white px-0'
                            />
                          </td>
                          <td className='p-2 bg-white border-y border-gray-100 group-hover:border-gray-200 align-middle'>
                            <FloatingLabelSelect
                                value={levels.find(
                                  (l) => l.value === record.level,
                                )}
                                onChange={handleSelectChange(index, 'level')}
                                options={levels}
                                placeholder='Select Level'
                            />
                          </td>
                          <td className='p-2 bg-white border-y border-gray-100 group-hover:border-gray-200 align-middle'>
                            <StyledInput
                                type='number'
                                value={record.numberOfQuestions}
                                onChange={handleInputChange(
                                  index,
                                  'numberOfQuestions',
                                )}
                                className='text-center border-transparent bg-transparent hover:bg-white focus:bg-white px-0'
                            />
                          </td>
                          <td className='p-2 bg-white border-y border-gray-100 group-hover:border-gray-200 align-middle'>
                            <StyledInput
                                type='number'
                                value={record.points}
                                onChange={handleInputChange(index, 'points')}
                                className='text-center border-transparent bg-transparent hover:bg-white focus:bg-white px-0'
                            />
                          </td>
                          <td className='p-2 bg-white border-y border-gray-100 group-hover:border-gray-200 align-middle'>
                            <StyledInput
                                type='number'
                                value={record.time}
                                onChange={handleInputChange(index, 'time')}
                                className='text-center text-gray-500 border-transparent bg-transparent hover:bg-white focus:bg-white px-0'
                            />
                          </td>
                          <td className='p-2 bg-white border-y border-gray-100 group-hover:border-gray-200 align-middle'>
                            <FloatingLabelSelect
                                value={questionTypesList.find(
                                  (t) => t.value === record.questionType,
                                )}
                                onChange={handleSelectChange(
                                  index,
                                  'questionType',
                                )}
                                options={questionTypesList}
                                placeholder='Select Type'
                            />
                          </td>
                          <td className='p-2 rounded-r-lg bg-white border-y border-r border-gray-100 group-hover:border-gray-200 text-center align-middle'>
                            <button
                                onClick={() => handleRemoveSkill(index)}
                                className='text-gray-300 hover:text-red-500 p-2 rounded-md hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100'
                            >
                              <TrashIcon className='w-4 h-4' />
                            </button>
                          </td>
                        </tr>
                      )) }
                    </tbody>
                  </table>
                ) : (
                  <div className='flex flex-col items-center justify-center min-h-44 sm:min-h-64 text-center rounded-xl border-2 border-dashed border-gray-200 my-3 sm:m-4 bg-gray-50/30 px-4 py-6'>
                    <div className='p-3 sm:p-4 bg-white rounded-full shadow-sm mb-3'>
                      <AddIcon className='w-7 h-7 sm:w-8 sm:h-8 text-gray-300' />
                    </div>
                    <p className='text-gray-900 font-semibold text-base'>
                      { t(QB.noSkillsTitle) }
                    </p>
                    <p className='max-w-[240px] text-gray-500 text-sm mt-1 mb-4'>
                      { t(QB.noSkillsHint) }
                    </p>
                    <Button
                        variant='primary'
                        label={t(QB.addFirstSkill)}
                        onClick={addNewSkillRow}
                        icon={<AddIcon />}
                        hasIcon
                        className='shadow-sm'
                    />
                  </div>
                ) }
              </div>

              { /* Table Footer Actions */ }
              <div className='px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50 flex flex-col gap-3 rounded-b-xl sm:flex-row sm:items-center sm:justify-between'>
                <Button
                    variant='link'
                    label={t(QB.addAnotherSkill)}
                    onClick={addNewSkillRow}
                    className='w-full text-blue-600 font-semibold hover:text-blue-700 p-0 sm:w-auto'
                />
                <Button
                    variant='primary'
                    label={isSaving ? t(QB.saving) : t(QB.saveSkills)}
                    onClick={saveSkills}
                    disabled={
                      isSaving || !hasUnsavedChanges || generatingQuestions
                    }
                    className='w-full shadow-sm sm:w-auto'
                />
              </div>
            </div>
          </div>

          { /* RIGHT PANEL: Sidebar */ }
          <div className='w-full lg:w-3/12 flex-shrink-0 flex flex-col gap-6'>
            { /* Settings Card */ }
            <div className='bg-white rounded-sm shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] border border-gray-200 p-4 sm:p-5'>
              <h4 className='text-xs font-bold text-gray-400 uppercase tracking-wider mb-4'>
                { t(QB.configuration) }
              </h4>

              <div className='space-y-5'>
                <div>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>
                    { t(QB.jobTitle) }
                  </label>
                  <div className='relative group'>
                    <StyledInput
                        value={editedJobTitle}
                        onChange={(e) => setEditedJobTitle(e.target.value)}
                        className='pl-9 bg-gray-50 focus:bg-white'
                        placeholder='e.g. Frontend Developer'
                    />
                    <BriefCaseIcon className='w-4 h-4 text-gray-400 absolute left-3 top-3 group-hover:text-blue-500 transition-colors' />
                  </div>
                  <p className='text-xs text-gray-500 mt-2 leading-relaxed'>
                    { t(QB.jobTitleHint) }
                  </p>
                </div>

                <div className='pt-4 border-t border-gray-100'>
                  <Button
                      variant='secondary'
                      label={
                        generatingQuestions
                          ? t(QB.generating)
                          : t(QB.autoGenerate)
                      }
                      onClick={generateQuestions}
                      disabled={
                        generatingQuestions ||
                      isSaving ||
                      records.length === 0 ||
                      hasUnsavedChanges ||
                      autoAssessment?.status === AutoBuildCompleteStatus
                      }
                      className={`w-full justify-center py-2.5 bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100 hover:border-blue-200 shadow-sm ${
                        generatingQuestions ? 'opacity-70' : ''
                      } transition-all font-medium`}
                      icon={
                        !generatingQuestions && <MagicIcons className='w-4 h-4' />
                      }
                      hasIcon
                  />
                </div>
              </div>
            </div>

            { /* Suggestions Panel - Replaces Need Help */ }
            <div className='bg-white rounded-sm shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] border border-gray-200 flex flex-col overflow-hidden flex-grow max-h-[320px] sm:max-h-[400px]'>
              <div className='p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center'>
                <h4 className='text-sm font-bold text-gray-800 flex items-center gap-2'>
                  <LightningIcons className='w-4 h-4 text-purple-500' />
                  { t(QB.suggestedSkills) }
                </h4>
                { skillsList.length > 0 && (
                  <button
                      onClick={handleAddAllSuggestions}
                      className='text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-full transition-colors'
                  >
                    { t(QB.selectAll) }
                  </button>
                ) }
              </div>

              <div className='p-2 overflow-y-auto custom-scrollbar flex-grow'>
                { skillsList.length > 0 ? (
                  <div className='flex flex-col gap-1'>
                    { skillsList.map((skill) => (
                      <button
                          key={skill}
                          onClick={() => handleAddSkill(skill)}
                          className='group flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 rounded-lg text-left transition-colors border border-transparent hover:border-blue-100'
                      >
                        <span className='text-gray-700 font-medium text-sm group-hover:text-blue-700'>
                          { skill }
                        </span>
                        <div className='w-5 h-5 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center group-hover:bg-blue-100 group-hover:text-blue-600 transition-all'>
                          <svg
                              className='w-3 h-3'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M12 4v16m8-8H4'
                            ></path>
                          </svg>
                        </div>
                      </button>
                    )) }
                  </div>
                ) : (
                  <div className='p-6 text-center text-gray-400 text-sm'>
                    { t(QB.noSuggestions) }
                  </div>
                ) }
              </div>

              <div className='p-3 bg-gray-50 border-t border-gray-100 text-center'>
                <button className='text-xs text-blue-600 font-semibold hover:text-blue-800 hover:underline'>
                  { t(QB.refreshSuggestions) }
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
